import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { EditorState } from '@/types/editor.types';
import { LinkedInFormatter } from '@/lib/formatters/linkedinFormatter';
import { Delta } from 'quill/core';

// Helper to create initial Delta
// We cast to any to avoid strict type checks for initial state if Delta constructor is not easily accessible or if we want a simple object
const emptyDelta = { ops: [] } as unknown as Delta;

export const useEditorStore = create<EditorState>()(
  immer((set, get) => ({
    content: emptyDelta,
    htmlContent: '',
    plainTextContent: '',
    quillInstance: null,
    canUndo: false,
    canRedo: false,

    setContent: (content) => {
      set((state) => {
        state.content = content;
        // Updating html and plain text relies on quill instance usually, 
        // but we can try to keep them in sync via event listeners in the component.
        // However, the store action `setContent` might be called from outside.
        // Ideally, we subscribe to Quill's text-change event and call a specific action 
        // that updates all these.
      });
    },

    setQuillInstance: (instance) => {
      set((state) => {
        // We need to cast to any or unknown first because Quill type might be complex with proxies
        state.quillInstance = instance as any; 
      });
      
      // Setup listeners
      instance.on('text-change', () => {
        const content = instance.getContents();
        const html = instance.root.innerHTML;
        const text = instance.getText(); // Quill's getText returns plain text
        
        // We can optimize this by not updating state on every keystroke if performance is bad,
        // but for this app it should be fine.
        set((state) => {
          state.content = content;
          state.htmlContent = html;
          state.plainTextContent = text;
          state.canUndo = instance.history ? (instance.history as any).stack.undo.length > 0 : false;
          state.canRedo = instance.history ? (instance.history as any).stack.redo.length > 0 : false;
        });
      });
    },

    getContentAsHTML: () => {
      return get().htmlContent;
    },

    getContentAsPlainText: () => {
      // Use our custom formatter for LinkedIn optimization
      return LinkedInFormatter.convertToLinkedInFormat(get().htmlContent);
    },

    clearContent: () => {
      const { quillInstance } = get();
      if (quillInstance) {
        quillInstance.setText('');
      }
    },

    undo: () => {
      const { quillInstance } = get();
      if (quillInstance && (quillInstance.history as any)) {
        (quillInstance.history as any).undo();
      }
    },

    redo: () => {
      const { quillInstance } = get();
      if (quillInstance && (quillInstance.history as any)) {
        (quillInstance.history as any).redo();
      }
    }
  }))
);
